import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import apiClient from '../lib/apiClient';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name?: string) => Promise<{ isPending?: boolean; message?: string } | void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (redirectAfterLogin?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // IMPORTANT: Set up auth listener BEFORE calling getSession()
    // This ensures we don't miss any auth events during initialization
    // See: https://supabase.com/docs/guides/auth/sessions
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Log detailed session info for debugging
      const sessionInfo = session ? {
        userId: session.user?.id,
        expiresAt: session.expires_at,
        expiresIn: session.expires_at ? session.expires_at - Math.floor(Date.now() / 1000) : null,
        hasRefreshToken: !!session.refresh_token,
      } : null;
      
      console.log('[AuthContext] Auth state changed:', event, sessionInfo);
      
      // Update state for all auth events
      setSession(session);
      setUser(session?.user ?? null);
      
      // Handle specific events
      switch (event) {
        case 'INITIAL_SESSION':
          console.log('[AuthContext] Initial session loaded from storage');
          break;
        case 'SIGNED_IN':
          console.log('[AuthContext] User signed in');
          break;
        case 'SIGNED_OUT':
          // Log why we're signing out - this helps debug unexpected logouts
          console.warn('[AuthContext] User signed out - check if this was intentional');
          break;
        case 'TOKEN_REFRESHED':
          console.log('[AuthContext] Token refreshed, new expiry:', session?.expires_at);
          break;
        case 'USER_UPDATED':
          console.log('[AuthContext] User data updated');
          break;
      }
      
      setLoading(false);
    });

    // Get initial session after setting up listener
    // This triggers INITIAL_SESSION event if session exists in storage
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('[AuthContext] Error getting initial session:', error);
      } else if (session) {
        console.log('[AuthContext] Session found, expires_at:', session.expires_at);
      } else {
        console.log('[AuthContext] No existing session found');
      }
      // Note: State is already set by onAuthStateChange INITIAL_SESSION event
      // This is just for logging/debugging
      setLoading(false);
    });

    // Note: We do NOT manually refresh tokens here anymore.
    // The Supabase SDK with autoRefreshToken: true handles this automatically.
    // Manual refresh causes "refresh_token_already_used" errors due to race conditions
    // between our code and the SDK's internal refresh mechanism.

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const data = await apiClient.post('/auth/register', {
        email,
        password,
        confirm_password: password,
        name: name || null,
      }, { skipAuth: true });

      // Check if response indicates pending approval
      if (data.approval_status === 'pending') {
        // Return special indicator for pending status
        return { isPending: true, message: data.message };
      }

      // Auto-approved - set session with tokens from backend
      if (data.access_token) {
        const { error } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });

        if (error) throw error;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to sign up');
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const data = await apiClient.post('/auth/login', {
        email,
        password,
      }, { skipAuth: true });

      // Sign in with Supabase using the tokens from backend
      if (data.access_token) {
        const { error } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });

        if (error) throw error;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to sign in');
    }
  };

  const signInWithGoogle = async (redirectAfterLogin?: string) => {
    try {
      // Build redirect URL - always use simple callback URL
      // Store OAuth redirect in sessionStorage since Supabase may strip query params during Google OAuth
      const redirectTo = `${window.location.origin}/auth/callback`;

      if (redirectAfterLogin) {
        // Persist redirect URL in sessionStorage - will be retrieved in AuthCallback
        // This is needed because Supabase often loses query params during OAuth round-trip
        sessionStorage.setItem('oauth_redirect_after_login', redirectAfterLogin);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
        },
      });

      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to sign in with Google');
    }
  };

  const signOut = async () => {
    try {
      // Since we use backend-issued tokens (via setSession), Supabase's server
      // doesn't have a corresponding session to invalidate. Using signOut()
      // causes 403 errors. Instead, we clear the session locally.
      
      // Clear localStorage directly to avoid server call
      const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
      localStorage.removeItem(storageKey);
      
      // Clear local state
      setSession(null);
      setUser(null);
      
      console.log('[AuthContext] Session cleared locally');
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message || 'Failed to sign out');
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
